import { sagaLabel, canonRole } from '../chunk-WVZXLYX3.js';
import { walkPages } from '../chunk-XR6TNFQL.js';
import { unwrapData } from '../chunk-HIWBYL4O.js';
import '../chunk-HMF6VH2N.js';
import '../chunk-HCZGW64N.js';
import { rowsFromWire } from '../chunk-BFUJOKOR.js';
import { normalizeAddress, isValidAddress } from '../chunk-7ZM2NOG4.js';
import '../chunk-43SJKO57.js';
import { NATIVE_TOKEN } from '../chunk-WVY3FUKD.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';

// src/grouping-rules/wire.ts
function asRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value : {};
}
function asStringOr(value, fallback = "") {
  return value == null ? fallback : String(value);
}
function asNullableString(value) {
  return value == null ? null : String(value);
}
function asOptString(value) {
  return value == null ? void 0 : String(value);
}
function asStringList(value) {
  return Array.isArray(value) ? value.filter((v) => typeof v === "string") : [];
}

// src/grouping-rules/source.ts
var STANDING_STATES = [
  "in_effect",
  "shadowed",
  "disabled",
  "invalid",
  "not_applicable"
];
var BUILTIN_STATES = [
  "automatic",
  "in_effect",
  "replaced",
  "replaced_on_chains",
  "turned_off"
];
function num(value, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function standingFromWire(raw) {
  if (!raw || typeof raw !== "object") return null;
  const s = raw;
  const state = STANDING_STATES.find((known) => known === s.state);
  if (!state) return null;
  return {
    state,
    sagaKind: asStringOr(s.saga_kind),
    position: num(s.position, 0),
    chainIds: asStringList(s.chain_ids),
    exceptChainIds: asStringList(s.except_chain_ids),
    shadowedBy: asStringList(s.shadowed_by),
    problems: asStringList(s.problems),
    takesOverIfDeleted: asStringList(s.takes_over_if_deleted)
  };
}
function builtinStandingFromWire(raw) {
  if (!raw || typeof raw !== "object") return null;
  const s = raw;
  const state = BUILTIN_STATES.find((known) => known === s.state);
  if (!state) return null;
  return {
    state,
    replacedBy: rowsFromWire(s.replaced_by).map((r) => ({
      id: asStringOr(r.id),
      name: asNullableString(r.name),
      scope: asStringOr(r.scope),
      chainIds: asStringList(r.chain_ids)
    })),
    chainIds: asStringList(s.chain_ids),
    reason: asNullableString(s.reason),
    offBy: asNullableString(s.off_by)
  };
}
function builtinFromWire(raw) {
  const spec = asRecord(raw.spec);
  return {
    key: asStringOr(raw.key),
    sagaKind: asStringOr(raw.saga_kind),
    origin: asStringOr(raw.origin),
    sourceId: asStringOr(raw.source_id),
    name: asStringOr(raw.name, asStringOr(raw.saga_kind)),
    protocolId: asNullableString(raw.protocol_id),
    idLabel: asNullableString(raw.id_label),
    relationshipType: asNullableString(raw.relationship_type),
    linkageModel: asNullableString(raw.linkage_model),
    description: asNullableString(raw.description),
    completeRoles: asStringList(raw.complete_roles),
    spec: Object.keys(spec).length > 0 ? spec : null,
    overridable: raw.overridable !== false,
    policySwitch: asNullableString(raw.policy_switch),
    recipeCount: num(raw.recipe_count, 1),
    standing: builtinStandingFromWire(raw.standing)
  };
}
function ruleFromWire(raw) {
  return {
    id: asStringOr(raw.id),
    name: asStringOr(raw.name),
    description: asNullableString(raw.description),
    scope: asStringOr(raw.scope, "tenant"),
    enabled: raw.enabled !== false,
    priority: num(raw.priority, 100),
    sagaKind: asNullableString(raw.saga_kind),
    relationshipType: asNullableString(raw.relationship_type),
    chainIds: asStringList(raw.chain_ids),
    spec: asRecord(raw.spec),
    matchCount: num(raw.match_count, 0),
    lastMatchedAt: typeof raw.last_matched_at === "number" ? raw.last_matched_at : null,
    updatedAt: typeof raw.updated_at === "number" ? raw.updated_at : null,
    updatedBy: asNullableString(raw.updated_by),
    standing: standingFromWire(raw.standing),
    canEdit: typeof raw.can_edit === "boolean" ? raw.can_edit : void 0
  };
}
function toWire(input) {
  const body = {};
  if (input.name !== void 0) body.name = input.name;
  if (input.description !== void 0) body.description = input.description;
  if (input.scope !== void 0) body.scope = input.scope;
  if (input.enabled !== void 0) body.enabled = input.enabled;
  if (input.priority !== void 0) body.priority = input.priority;
  if (input.sagaKind !== void 0) body.saga_kind = input.sagaKind;
  if (input.chainIds !== void 0) body.chain_ids = input.chainIds;
  if (input.spec !== void 0) body.spec = input.spec;
  return body;
}
function optionsFromWire(raw) {
  return rowsFromWire(raw).map((o) => ({
    value: asStringOr(o.value),
    label: asStringOr(o.label ?? o.value),
    description: asOptString(o.description)
  }));
}
function fieldsFromWire(raw) {
  return rowsFromWire(raw).map((f) => ({
    name: asStringOr(f.name),
    type: asStringOr(f.type, "string"),
    required: f.required === true,
    description: asOptString(f.description)
  }));
}
var LOCATION_KINDS = ["text", "integer", "flag", "object"];
function locationsFromWire(raw) {
  return rowsFromWire(raw).map((l) => ({
    name: asStringOr(l.name),
    kind: LOCATION_KINDS.find((k) => k === l.kind) ?? "text",
    description: asOptString(l.description)
  }));
}
function scopesFromWire(raw) {
  return rowsFromWire(raw).map((o) => ({
    value: asStringOr(o.value),
    label: asStringOr(o.label ?? o.value),
    description: asOptString(o.description),
    internalOnly: o.internal_only === true,
    authorable: typeof o.authorable === "boolean" ? o.authorable : void 0
  }));
}
function catalogFromWire(raw) {
  const roles = asRecord(raw.roles);
  return {
    relationshipTypes: optionsFromWire(raw.relationship_types),
    linkageModels: optionsFromWire(raw.linkage_models),
    truthLocations: optionsFromWire(raw.truth_locations),
    transforms: optionsFromWire(raw.transforms),
    defaultTransform: asStringOr(raw.default_transform, "lower"),
    roles: {
      canonical: asStringList(roles.canonical),
      known: asStringList(roles.known),
      defaultCompleteRoles: asStringList(roles.default_complete_roles)
    },
    specFields: fieldsFromWire(raw.spec_fields),
    selectorFields: fieldsFromWire(raw.selector_fields),
    valueSourceFields: fieldsFromWire(raw.value_source_fields),
    valueLocations: locationsFromWire(raw.value_locations),
    scopes: scopesFromWire(raw.scopes),
    flow: flowCatalogFromWire(raw.flow)
  };
}
function flowCatalogFromWire(raw) {
  const flow = asRecord(raw);
  const relationshipType = asStringOr(flow.relationship_type);
  if (!relationshipType) return null;
  return {
    relationshipType,
    matches: optionsFromWire(flow.matches),
    directions: optionsFromWire(flow.directions),
    groupBys: optionsFromWire(flow.group_bys),
    fields: fieldsFromWire(flow.fields),
    nativeAsset: asStringOr(flow.native_asset, NATIVE_TOKEN),
    maxAddresses: num(flow.max_addresses, 500)
  };
}
function createApiGroupingRulesSource(client, { url = (_verb, path) => path, listQuery } = {}) {
  const invalidate = () => {
    client.invalidateMatching("/grouping/rules");
    client.invalidateMatching("/grouping/builtins");
  };
  return {
    async specCatalog() {
      const res = await client.get(url("GET", "/grouping/spec-catalog"));
      return catalogFromWire(asRecord(unwrapData(res)));
    },
    async builtins() {
      let data;
      try {
        data = unwrapData(await client.get(url("GET", "/grouping/builtins")));
      } catch (error) {
        if (error.statusCode === 404) return null;
        throw error;
      }
      return rowsFromWire(asRecord(data).builtins).map(builtinFromWire);
    },
    async list() {
      return (await walkPages(client, url("GET", "/grouping/rules"), listQuery ? { query: listQuery } : {})).map(ruleFromWire);
    },
    async create(input) {
      const res = await client.post(url("ANY", "/grouping/rules"), toWire(input));
      const saved = ruleFromWire(asRecord(unwrapData(res)));
      invalidate();
      return saved;
    },
    async update(id, input) {
      const res = await client.patch(
        url("ANY", `/grouping/rules/${encodeURIComponent(id)}`),
        toWire(input)
      );
      const saved = ruleFromWire(asRecord(unwrapData(res)));
      invalidate();
      return saved;
    },
    async remove(id) {
      unwrapData(
        await client.delete(url("ANY", `/grouping/rules/${encodeURIComponent(id)}`))
      );
      invalidate();
    }
  };
}

// src/grouping-rules/policy.ts
var identity = (_verb, path) => path;
async function fetchGroupingPolicy(client, url = identity) {
  const body = unwrapData(await client.get(url("GET", "/grouping/policy"))) ?? {};
  const raw = body.result ?? body;
  const vocabulary = raw.vocabulary ?? {};
  return {
    tiers: Array.isArray(raw.tiers) ? raw.tiers : [],
    effective: raw.effective ?? {},
    origins: Array.isArray(raw.origins) ? raw.origins : [],
    problems: Array.isArray(raw.problems) ? raw.problems.map(String) : [],
    completionByKind: raw.completion_by_kind ?? {},
    completableKinds: Array.isArray(vocabulary.completable_kinds) ? vocabulary.completable_kinds.map(String) : []
  };
}
async function saveGroupingPolicy(client, tier, section, url = identity) {
  unwrapData(await client.put(url("ANY", "/grouping/policy"), { section }, { query: { tier } }));
  client.invalidateMatching("/grouping/policy");
  client.invalidateMatching("/grouping/builtins");
}
async function resetGroupingPolicy(client, tier, url = identity) {
  unwrapData(await client.delete(url("ANY", "/grouping/policy"), { query: { tier } }));
  client.invalidateMatching("/grouping/policy");
  client.invalidateMatching("/grouping/builtins");
}
function createApiGroupingPolicySource(client, { url = identity } = {}) {
  return {
    read: () => fetchGroupingPolicy(client, url),
    save: (tier, section) => saveGroupingPolicy(client, tier, section, url),
    reset: (tier) => resetGroupingPolicy(client, tier, url)
  };
}
function withKnob(section, path, value) {
  const [head, ...rest] = path;
  if (head === void 0) return section;
  const out = { ...section };
  if (rest.length === 0) {
    if (value === void 0) delete out[head];
    else out[head] = value;
    return out;
  }
  const child = out[head];
  const next = withKnob(
    child && typeof child === "object" && !Array.isArray(child) ? child : {},
    rest,
    value
  );
  if (Object.keys(next).length === 0) delete out[head];
  else out[head] = next;
  return out;
}
function knobAt(section, path) {
  let node = section ?? void 0;
  for (const key of path) {
    if (!node || typeof node !== "object") return void 0;
    node = node[key];
  }
  return node;
}
function originOf(view, path) {
  const dotted = path.join(".");
  return view.origins.find((o) => o.path === dotted);
}
function authoredRungs(view) {
  return view.tiers.filter(
    (t) => t.tier !== "base" && t.stored_section && Object.keys(t.stored_section).length > 0
  ).length;
}
function inheritedAt(view, tier, path) {
  let value;
  for (const rung of view.tiers) {
    if (rung.tier === tier) break;
    const set = knobAt(rung.section, path);
    if (set !== void 0) value = set;
  }
  return value;
}
function sectionWithKindSwitched(view, tier, sagaKind, on) {
  const stored = view.tiers.find((t) => t.tier === tier)?.stored_section ?? {};
  const own = knobAt(stored, ["disabled_saga_kinds"]);
  const inherited = inheritedAt(view, tier, ["disabled_saga_kinds"]);
  const current = (Array.isArray(own) ? own : Array.isArray(inherited) ? inherited : []).map(String);
  const next = on ? current.filter((kind) => kind !== sagaKind) : [.../* @__PURE__ */ new Set([...current, sagaKind])];
  const inheritedList = (Array.isArray(inherited) ? inherited : []).map(String);
  return withKnob(stored, ["disabled_saga_kinds"], sameSet(next, inheritedList) ? void 0 : next);
}
function sameSet(a, b) {
  return a.length === b.length && a.every((k) => b.includes(k));
}

// src/grouping-rules/builtins.ts
function builtinOverrideDraft(recipe, scope) {
  const identity2 = {
    saga_kind: recipe.sagaKind,
    ...recipe.idLabel ? { id_label: recipe.idLabel.split(",")[0].trim() } : {},
    ...recipe.relationshipType ? { type: recipe.relationshipType } : {},
    ...recipe.linkageModel ? { linkage_model: recipe.linkageModel } : {},
    ...recipe.completeRoles.length ? { complete_roles: recipe.completeRoles } : {},
    ...recipe.protocolId ? { protocol_id: recipe.protocolId } : {}
  };
  return {
    name: `${recipe.name} (override)`,
    description: `Replaces the built-in ${recipe.name} recipe (${recipe.sourceId}).`,
    scope,
    enabled: true,
    priority: 100,
    sagaKind: recipe.sagaKind,
    chainIds: [],
    // The recipe's own fields win over the identity where both exist.
    spec: recipe.spec ? { ...identity2, ...recipe.spec } : identity2
  };
}
function overrideStartsFromTheRecipe(recipe) {
  return recipe.spec !== null;
}
function recipeCanBeSwitched(recipe) {
  return recipe.origin !== "context_detector" && recipe.standing?.offBy !== "enabled";
}
function sectionWithRecipeSwitched(view, tier, recipe, on) {
  const policySwitch = recipe.policySwitch;
  const setSwitch = (section, value) => withKnob(section, [policySwitch], inheritedAt(view, tier, [policySwitch]) === value ? void 0 : value);
  if (!on) {
    return policySwitch ? setSwitch(view.tiers.find((t) => t.tier === tier)?.stored_section ?? {}, false) : sectionWithKindSwitched(view, tier, recipe.sagaKind, false);
  }
  const listed = sectionWithKindSwitched(view, tier, recipe.sagaKind, true);
  return policySwitch ? setSwitch(listed, true) : listed;
}

// src/grouping-rules/focus.ts
var AUTHORED_RULE_PREFIX = "grouping_rule:";
var PROTOCOL_DEFINITION_PREFIX = "protocol_definition:";
function protocolRecipeSourceId(protocolId) {
  return `${PROTOCOL_DEFINITION_PREFIX}${protocolId}`;
}
function protocolIdOfSource(sourceId) {
  return sourceId.startsWith(PROTOCOL_DEFINITION_PREFIX) ? sourceId.slice(PROTOCOL_DEFINITION_PREFIX.length) : sourceId;
}
function ruleIdOfSource(sourceId) {
  if (!sourceId?.startsWith(AUTHORED_RULE_PREFIX)) return void 0;
  return sourceId.slice(AUTHORED_RULE_PREFIX.length) || void 0;
}
function unversioned(sourceId) {
  const at = sourceId.lastIndexOf("@");
  return at > 0 ? sourceId.slice(0, at) : sourceId;
}
function recipesForSource(recipes, sourceId) {
  if (!sourceId) return { recipes: [], exact: true };
  const exact = recipes.filter((recipe) => recipe.sourceId === sourceId);
  if (exact.length > 0) return { recipes: exact, exact: true };
  const stem = unversioned(sourceId);
  return { recipes: recipes.filter((recipe) => unversioned(recipe.sourceId) === stem), exact: false };
}
function ruleForFocus(rules, focus) {
  return focus?.ruleId ? rules.find((rule) => rule.id === focus.ruleId) : void 0;
}

// src/grouping-rules/standing.ts
function flowSummary(spec) {
  const flow = spec?.flow;
  if (!flow || typeof flow !== "object") return null;
  const f = flow;
  const count = Array.isArray(f.addresses) ? f.addresses.length : 0;
  const noun = `${count} address${count === 1 ? "" : "es"}`;
  const touching = f.match === "touching";
  const direction = f.direction === "outgoing" ? "Sent from " : f.direction === "incoming" ? "Received by " : "";
  const counterparties = Array.isArray(f.counterparties) ? f.counterparties.length : 0;
  const head = touching ? `${direction || "Touching "}${noun}${counterparties ? ` \xB7 with ${counterparties} counterpart${counterparties === 1 ? "y" : "ies"}` : ""}` : `Between ${noun}`;
  const groups = {
    rule: "one group",
    address: "a group per address",
    pair: "a group per pair",
    counterparty: "a group per counterparty"
  };
  const assets = Array.isArray(f.assets) && f.assets.length ? ` \xB7 ${f.assets.length} asset${f.assets.length === 1 ? "" : "s"}` : "";
  return `${head} \xB7 ${groups[String(f.group_by ?? "rule")] ?? "one group"}${assets}`;
}
function standingOf(rule) {
  return rule.standing?.state ?? "unknown";
}
function neverMatchedNote(rule) {
  const standing = rule.standing;
  switch (standing?.state) {
    case "shadowed":
      return { warn: false, title: "Never matched \u2014 expected: a more specific rule wins for this saga kind, so this one never applies." };
    case "disabled":
      return { warn: false, title: "Never matched \u2014 expected: the rule is turned off." };
    case "invalid":
      return { warn: false, title: "Never matched \u2014 the engine cannot read this rule's spec, so it never competes. Fix the spec." };
    case "not_applicable":
      return { warn: false, title: "Never matched in your runs \u2014 expected: this is another user's private rule." };
    case "in_effect":
      return {
        warn: true,
        title: standing.chainIds.length > 0 ? `This rule has never been credited with a group. It applies only on ${standing.chainIds.join(", ")}, so transactions anywhere else cannot match it however well the spec fits. Otherwise nothing has matched it yet.` : "This rule has never been credited with a group \u2014 nothing it selects has been seen by a run yet, or its selectors do not match what the protocol emits."
      };
    default:
      return { warn: false, title: "Never matched. Whether this rule should have applied could not be determined." };
  }
}
function deletionImpact(rule, rules) {
  const standing = rule.standing;
  const kind = standing?.sagaKind || rule.sagaKind || "";
  if (!standing) return { kind, known: false, wasInEffect: false, successors: [] };
  const byId = new Map(rules.map((r) => [r.id, r]));
  const successors = standing.takesOverIfDeleted.map((id) => byId.get(id)).filter((r) => r !== void 0);
  return { kind, known: true, wasInEffect: standing.state === "in_effect", successors };
}
function groupBySagaKind(rules) {
  const buckets = /* @__PURE__ */ new Map();
  for (const rule of rules) {
    const kind = rule.standing?.sagaKind || rule.sagaKind || "";
    const list = buckets.get(kind) ?? [];
    list.push(rule);
    buckets.set(kind, list);
  }
  const byLadder = (a, b) => {
    const left = a.standing?.position;
    const right = b.standing?.position;
    if (left === void 0 || right === void 0) {
      return left === right ? 0 : left === void 0 ? 1 : -1;
    }
    return left - right;
  };
  return Array.from(buckets.entries()).map(([kind, list]) => ({ kind, rules: list.slice().sort(byLadder) })).sort((a, b) => sagaLabel(a.kind).localeCompare(sagaLabel(b.kind)));
}

// src/grouping-rules/ruleForm.ts
function scopeOptions(scopes, canAuthorSystem, current, allowed) {
  const offered = scopes.filter(
    (scope) => (
      // The API's answer for this caller when it sends one; the host's guess
      // otherwise. The dashboard's guess (`pipelines:admin`) offered `system`
      // to callers the API then refused — it wants an internal caller.
      (scope.authorable ?? (canAuthorSystem || !scope.internalOnly)) && (!allowed || allowed.includes(scope.value))
    )
  );
  if (current && !offered.some((scope) => scope.value === current)) {
    const known = scopes.find((scope) => scope.value === current);
    offered.push(known ?? { value: current, label: current, internalOnly: false });
  }
  return offered;
}
function requiredSpecFields(catalog) {
  return (catalog?.specFields ?? []).filter((f) => f.required).map((f) => f.name);
}
function parseSpec(text, required = []) {
  const trimmed = text.trim();
  if (!trimmed) return { error: "A rule with no spec never matches anything." };
  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return { error: `Not valid JSON: ${err instanceof Error ? err.message : String(err)}` };
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { error: "The spec must be a JSON object." };
  }
  const spec = parsed;
  const missing = required.filter((field) => {
    const value = spec[field];
    return value === void 0 || value === null || value === "";
  });
  if (missing.length > 0) {
    return {
      error: `Missing required field${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}. The engine would skip this rule.`
    };
  }
  return { spec };
}
function validate(values, required = []) {
  const errors = {};
  if (!values.name.trim()) errors.name = "Give the rule a name you will recognise in the list.";
  if (!Number.isInteger(values.priority) || values.priority < 0) {
    errors.priority = "Priority is a whole number; lower wins within a scope.";
  }
  const { error } = parseSpec(values.specText, required);
  if (error) errors.specText = error;
  return errors;
}
function isValid(values, required = []) {
  return Object.keys(validate(values, required)).length === 0;
}
function toCreateInput(values) {
  const { spec } = parseSpec(values.specText);
  const sagaKind = String(spec?.saga_kind ?? "").trim() || values.sagaKind.trim() || null;
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    scope: values.scope,
    enabled: values.enabled,
    priority: values.priority,
    sagaKind,
    chainIds: values.chainIds,
    spec
  };
}
function toUpdateInput(values) {
  const input = toCreateInput(values);
  const { scope: _scope, ...rest } = input;
  return rest;
}

// src/grouping-rules/specDraft.ts
function locationTable(catalog) {
  return Object.fromEntries((catalog?.valueLocations ?? []).map((l) => [l.name, l.kind]));
}
var DEFAULT_LOCATION = "path";
var PART_MODIFIERS = ["byte_offset", "byte_length", "transform"];
function partKeys(locations) {
  return /* @__PURE__ */ new Set([...Object.keys(locations), ...PART_MODIFIERS]);
}
var SELECTOR_KEYS = /* @__PURE__ */ new Set([
  "role",
  "event",
  "topic0",
  "abci_event",
  "include_call",
  "exclude_log_addresses",
  "value"
]);
var SPEC_KEYS = /* @__PURE__ */ new Set([
  "type",
  "linkage_model",
  "id_label",
  "saga_kind",
  "truth_location",
  "complete_roles",
  "description",
  "selectors",
  "flow",
  "open_ended",
  "max_members"
]);
var FLOW_KEYS = /* @__PURE__ */ new Set([
  "addresses",
  "match",
  "counterparties",
  "direction",
  "group_by",
  "assets",
  "include_calls"
]);
function unmodelled(raw, known) {
  const out = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!known.has(key)) out[key] = value;
  }
  return out;
}
var DEFAULT_FLOW_ID_LABEL = "address_set";
function emptyPart(catalog) {
  return {
    location: DEFAULT_LOCATION,
    operand: "",
    byteOffset: "",
    byteLength: "",
    transform: catalog?.defaultTransform ?? "lower"
  };
}
function emptySelector(catalog, role = "") {
  return {
    role,
    event: "",
    topic0: "",
    abciEvent: "",
    includeCall: false,
    excludeLogAddresses: "",
    value: [emptyPart(catalog)]
  };
}
function emptyFlow(catalog) {
  return {
    addresses: "",
    match: catalog?.flow?.matches[0]?.value ?? "between",
    counterparties: "",
    direction: catalog?.flow?.directions[0]?.value ?? "any",
    groupBy: catalog?.flow?.groupBys[0]?.value ?? "rule",
    assets: "",
    includeCalls: true
  };
}
function emptyDraft(catalog) {
  const [source, destination] = catalog?.roles.canonical ?? ["source", "destination"];
  return {
    matchBy: "id",
    flow: emptyFlow(catalog),
    openEnded: false,
    maxMembers: "",
    type: catalog?.relationshipTypes[0]?.value ?? "cross_chain_message",
    linkageModel: catalog?.linkageModels[0]?.value ?? "deterministic",
    idLabel: "",
    sagaKind: "",
    truthLocation: catalog?.truthLocations[0]?.value ?? "decoded_args",
    completeRoles: (catalog?.roles.defaultCompleteRoles ?? ["source", "destination"]).join(", "),
    description: "",
    selectors: [
      emptySelector(catalog, source ?? "source"),
      emptySelector(catalog, destination ?? "destination")
    ],
    locations: locationTable(catalog)
  };
}
function splitList(text) {
  return text.split(",").map((part) => part.trim()).filter(Boolean);
}
function splitAddresses(text) {
  return text.split(/[\s,;]+/).map((part) => part.trim()).filter(Boolean);
}
function withMatchBy(draft, matchBy, catalog) {
  if (draft.matchBy === matchBy) return draft;
  const flowType = catalog?.flow?.relationshipType ?? "address_flow";
  const idType = catalog?.relationshipTypes[0]?.value ?? "cross_chain_message";
  return {
    ...draft,
    matchBy,
    type: matchBy === "addresses" ? flowType : draft.type === flowType ? idType : draft.type,
    idLabel: matchBy === "addresses" ? draft.idLabel || DEFAULT_FLOW_ID_LABEL : draft.idLabel === DEFAULT_FLOW_ID_LABEL ? "" : draft.idLabel
  };
}
function locationsIn(part, locations) {
  return Object.entries(locations).filter(([name, kind]) => {
    const value = part[name];
    if (kind === "flag") return value === true;
    if (kind === "integer") return value !== void 0 && value !== null;
    return Boolean(value);
  }).map(([name]) => name);
}
function locationOf(part, locations) {
  return locationsIn(part, locations)[0] ?? DEFAULT_LOCATION;
}
function operandOf(part, location, locations) {
  if (locations[location] === "flag") return "";
  const raw = part[location];
  return raw === void 0 || raw === null ? "" : locations[location] === "object" ? JSON.stringify(raw) : String(raw);
}
function specToDraft(spec, catalog) {
  const base = emptyDraft(catalog);
  const { locations } = base;
  const modelledPartKeys = partKeys(locations);
  const selectors = Array.isArray(spec.selectors) ? spec.selectors : [];
  const storedFlow = spec.flow && typeof spec.flow === "object" ? asRecord(spec.flow) : null;
  return {
    matchBy: storedFlow ? "addresses" : "id",
    flow: storedFlow ? {
      addresses: asStringList(storedFlow.addresses).join("\n"),
      match: asStringOr(storedFlow.match, base.flow.match),
      counterparties: asStringList(storedFlow.counterparties).join("\n"),
      direction: asStringOr(storedFlow.direction, base.flow.direction),
      groupBy: asStringOr(storedFlow.group_by, base.flow.groupBy),
      assets: asStringList(storedFlow.assets).join(", "),
      includeCalls: storedFlow.include_calls !== false,
      passthrough: unmodelled(storedFlow, FLOW_KEYS)
    } : base.flow,
    openEnded: spec.open_ended === true,
    maxMembers: typeof spec.max_members === "number" ? String(spec.max_members) : "",
    type: String(spec.type ?? base.type),
    linkageModel: String(spec.linkage_model ?? base.linkageModel),
    idLabel: String(spec.id_label ?? ""),
    sagaKind: String(spec.saga_kind ?? ""),
    truthLocation: String(spec.truth_location ?? base.truthLocation),
    completeRoles: (Array.isArray(spec.complete_roles) ? spec.complete_roles : []).join(", "),
    description: String(spec.description ?? ""),
    locations,
    passthrough: unmodelled(spec, SPEC_KEYS),
    selectors: selectors.length ? selectors.map((entry) => {
      const raw = asRecord(entry);
      const parts = Array.isArray(raw.value) ? raw.value : [];
      return {
        role: asStringOr(raw.role),
        event: asStringOr(raw.event),
        topic0: asStringOr(raw.topic0),
        abciEvent: asStringOr(raw.abci_event),
        includeCall: raw.include_call === true,
        excludeLogAddresses: asStringList(raw.exclude_log_addresses).join(", "),
        passthrough: unmodelled(raw, SELECTOR_KEYS),
        value: parts.length ? parts.map((entryPart) => {
          const p = asRecord(entryPart);
          const present = locationsIn(p, locations);
          const location = present[0] ?? DEFAULT_LOCATION;
          return {
            location,
            droppedLocations: present.slice(1),
            operand: operandOf(p, location, locations),
            byteOffset: p.byte_offset ? String(p.byte_offset) : "",
            byteLength: p.byte_length === void 0 || p.byte_length === null ? "" : String(p.byte_length),
            transform: asStringOr(p.transform, catalog?.defaultTransform ?? "lower"),
            passthrough: unmodelled(p, modelledPartKeys)
          };
        }) : [emptyPart(catalog)]
      };
    }) : base.selectors
  };
}
function partToWire(part, locations) {
  const out = {
    ...part.passthrough ?? {},
    transform: part.transform
  };
  const kind = locations[part.location] ?? "text";
  if (kind === "flag") {
    out[part.location] = true;
  } else if (kind === "integer") {
    out[part.location] = Number(part.operand);
  } else if (kind === "object") {
    try {
      out[part.location] = JSON.parse(part.operand);
    } catch {
      out[part.location] = null;
    }
  } else {
    out[part.location] = part.operand.trim();
  }
  if (part.location === "data_bytes_word") {
    if (part.byteOffset.trim()) out.byte_offset = Number(part.byteOffset);
    if (part.byteLength.trim()) out.byte_length = Number(part.byteLength);
  }
  return out;
}
function flowToWire(flow, touching) {
  const out = {
    ...flow.passthrough ?? {},
    addresses: splitAddresses(flow.addresses),
    match: flow.match,
    group_by: flow.groupBy,
    include_calls: flow.includeCalls
  };
  delete out.counterparties;
  delete out.direction;
  if (touching) {
    out.direction = flow.direction;
    const counterparties = splitAddresses(flow.counterparties);
    if (counterparties.length) out.counterparties = counterparties;
  }
  const assets = splitList(flow.assets);
  if (assets.length) out.assets = assets;
  else delete out.assets;
  return out;
}
function draftToSpec(draft) {
  const maxMembers = Number(draft.maxMembers.trim());
  const withLimits = (spec2) => {
    delete spec2.max_members;
    if (draft.maxMembers.trim() && Number.isInteger(maxMembers) && maxMembers > 0) {
      spec2.max_members = maxMembers;
    }
    if (draft.description.trim()) spec2.description = draft.description.trim();
    return spec2;
  };
  if (draft.matchBy === "addresses") {
    return withLimits({
      ...draft.passthrough ?? {},
      type: draft.type,
      linkage_model: draft.linkageModel,
      id_label: draft.idLabel.trim() || DEFAULT_FLOW_ID_LABEL,
      saga_kind: draft.sagaKind.trim(),
      flow: flowToWire(draft.flow, draft.flow.match === "touching"),
      open_ended: true
    });
  }
  const spec = {
    ...draft.passthrough ?? {},
    type: draft.type,
    linkage_model: draft.linkageModel,
    id_label: draft.idLabel.trim(),
    saga_kind: draft.sagaKind.trim(),
    truth_location: draft.truthLocation,
    selectors: draft.selectors.map((selector) => {
      const out = {
        ...selector.passthrough ?? {},
        role: selector.role.trim(),
        include_call: selector.includeCall,
        value: selector.value.map((part) => partToWire(part, draft.locations))
      };
      if (selector.event.trim()) out.event = selector.event.trim();
      if (selector.topic0.trim()) out.topic0 = selector.topic0.trim().toLowerCase();
      if (selector.abciEvent.trim()) out.abci_event = selector.abciEvent.trim();
      const excluded = splitList(selector.excludeLogAddresses).map((a) => a.toLowerCase());
      if (excluded.length) out.exclude_log_addresses = excluded;
      return out;
    })
  };
  const roles = splitList(draft.completeRoles);
  if (roles.length && !draft.openEnded) spec.complete_roles = roles;
  if (draft.openEnded) spec.open_ended = true;
  else delete spec.open_ended;
  return withLimits(spec);
}
function firstInvalidAddress(entries) {
  return entries.find((entry) => !isValidAddress(entry));
}
function validateFlow(draft, errors) {
  const flow = draft.flow;
  const touching = flow.match === "touching";
  const addresses = [...new Set(splitAddresses(flow.addresses).map((a) => normalizeAddress(a)))];
  const badAddress = firstInvalidAddress(splitAddresses(flow.addresses));
  if (badAddress) {
    errors.addresses = `\u201C${badAddress}\u201D is not an address.`;
  } else if (addresses.length === 0) {
    errors.addresses = "List the addresses this rule is about.";
  } else if (!touching && addresses.length < 2) {
    errors.addresses = "\u201CBetween\u201D needs two or more addresses.";
  }
  const badCounterparty = firstInvalidAddress(splitAddresses(flow.counterparties));
  if (touching && badCounterparty) {
    errors.counterparties = `\u201C${badCounterparty}\u201D is not an address.`;
  }
  const badAsset = splitList(flow.assets).find(
    (asset) => asset.toLowerCase() !== NATIVE_TOKEN && !isValidAddress(asset)
  );
  if (badAsset) {
    errors.assets = `\u201C${badAsset}\u201D is not a token contract (or \u201Cnative\u201D).`;
  }
  if (!touching && flow.groupBy === "counterparty") {
    errors.groupBy = "One group per counterparty needs \u201CTouching\u201D \u2014 between listed addresses there is no outside address.";
  }
}
function validateDraft(draft) {
  const errors = {};
  if (draft.maxMembers.trim()) {
    const n = Number(draft.maxMembers.trim());
    if (!Number.isInteger(n) || n < 1 || n > 5e3) {
      errors.maxMembers = "A whole number from 1 to 5000.";
    }
  }
  if (draft.matchBy === "addresses") {
    if (!draft.sagaKind.trim()) {
      errors.sagaKind = "The namespace this rule competes for. One rule applies per kind.";
    }
    validateFlow(draft, errors);
    return errors;
  }
  if (!draft.idLabel.trim()) {
    errors.idLabel = "Name the id the legs share \u2014 it is what the group is keyed on.";
  }
  if (!draft.sagaKind.trim()) {
    errors.sagaKind = "The namespace this rule competes for. One rule applies per kind.";
  }
  const declared = splitList(draft.completeRoles).map(canonRole).filter(Boolean);
  if (declared.length && !draft.openEnded) {
    const produced = new Set(
      draft.selectors.map((selector) => canonRole(selector.role)).filter(Boolean)
    );
    const unreachable = [...new Set(declared)].filter((role) => !produced.has(role));
    if (unreachable.length) {
      errors.completeRoles = `No selector produces ${unreachable.join(", ")}. The group would wait for a leg this rule cannot emit, then time out for good.`;
    }
  }
  const selectorErrors = {};
  draft.selectors.forEach((selector, index) => {
    if (!selector.role.trim()) {
      selectorErrors[index] = "Every selector names the leg it identifies.";
      return;
    }
    if (selector.value.length === 0) {
      selectorErrors[index] = "A selector with no value part resolves nothing.";
      return;
    }
    const incomplete = selector.value.some(
      (part) => draft.locations[part.location] !== "flag" && !part.operand.trim()
    );
    if (incomplete) {
      selectorErrors[index] = "Every value part needs something to read.";
      return;
    }
    const badNumber = selector.value.some(
      (part) => draft.locations[part.location] === "integer" && !Number.isInteger(Number(part.operand))
    );
    if (badNumber) selectorErrors[index] = "A word index is a whole number.";
    const badObject = selector.value.some((part) => {
      if (draft.locations[part.location] !== "object") return false;
      try {
        const value = JSON.parse(part.operand);
        return !value || typeof value !== "object" || Array.isArray(value);
      } catch {
        return true;
      }
    });
    if (badObject) selectorErrors[index] = "A structured location needs a JSON object.";
  });
  if (Object.keys(selectorErrors).length) errors.selectors = selectorErrors;
  return errors;
}
function draftIsValid(draft) {
  return Object.keys(validateDraft(draft)).length === 0;
}

export { DEFAULT_FLOW_ID_LABEL, authoredRungs, builtinFromWire, builtinOverrideDraft, createApiGroupingPolicySource, createApiGroupingRulesSource, deletionImpact, draftIsValid, draftToSpec, emptyDraft, emptyFlow, emptyPart, emptySelector, fetchGroupingPolicy, flowSummary, groupBySagaKind, inheritedAt, isValid, knobAt, locationOf, locationTable, neverMatchedNote, originOf, overrideStartsFromTheRecipe, parseSpec, protocolIdOfSource, protocolRecipeSourceId, recipeCanBeSwitched, recipesForSource, requiredSpecFields, resetGroupingPolicy, ruleForFocus, ruleIdOfSource, saveGroupingPolicy, scopeOptions, sectionWithKindSwitched, sectionWithRecipeSwitched, specToDraft, splitAddresses, standingOf, toCreateInput, toUpdateInput, validate, validateDraft, withKnob, withMatchBy };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map